import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import StripePaymentForm from '../components/StripePaymentForm';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  PaperAirplaneIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  CreditCardIcon,
  BanknotesIcon,
  QrCodeIcon,
  DocumentTextIcon,
  CurrencyDollarIcon,
  CalendarIcon,
  UserGroupIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

const SUPPORTED_PAYMENT_METHODS = ['stripe'];
const PAYMENT_METHOD_LABELS = {
  stripe: 'Stripe (Card)'
};

const Invoicing = () => {
  const { user } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showPaymentSuccess, setShowPaymentSuccess] = useState(false);
  const [remindTimingByInvoice, setRemindTimingByInvoice] = useState({});

  const authHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('token')}`
  });

  const fetchTeamsForOrganizations = async (organizationIds = []) => {
    const teamsById = new Map();
    for (const orgId of organizationIds) {
      try {
        const response = await fetch(`/api/teams?organizationId=${orgId}`, {
          headers: authHeaders()
        });
        if (!response.ok) continue;
        const data = await response.json();
        const orgTeams = Array.isArray(data) ? data : (Array.isArray(data?.teams) ? data.teams : []);
        orgTeams.forEach(team => {
          if (team?.id && !teamsById.has(team.id)) {
            teamsById.set(team.id, team);
          }
        });
      } catch (error) {
        console.warn(`Failed to fetch teams for organization ${orgId}:`, error);
      }
    }
    return Array.from(teamsById.values());
  };

  const fetchRosterRecipientsForTeams = async (teamsList = []) => {
    const recipients = [];
    const recipientIds = new Set();

    for (const team of teamsList) {
      try {
        const rosterResponse = await fetch(`/api/roster?teamId=${team.id}`, {
          headers: authHeaders()
        });

        if (!rosterResponse.ok) {
          if (rosterResponse.status === 403) continue;
          continue;
        }

        const rosterData = await rosterResponse.json();
        rosterData.forEach(member => {
          if (!member?.id || recipientIds.has(member.id)) return;
          recipientIds.add(member.id);
          recipients.push({
            id: member.id,
            name: member.name,
            email: member.email,
            role: member.role,
            type: 'user',
            teamName: team.name
          });
        });
      } catch (error) {
        console.warn(`Could not fetch roster for team ${team.id}:`, error);
      }
    }

    return recipients;
  };

  // Fetch recipients based on user role
  const fetchRecipients = async () => {
    try {
      console.log('Current user role:', user?.role);
      
      if (user?.role === 'app_manager' || user?.role === 'admin') {
        // App managers and admins can see all users
        const usersResponse = await fetch('/api/users', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (usersResponse.ok) {
          const usersData = await usersResponse.json();
          console.log('Fetched all users for admin:', usersData);
          setUsers(usersData);
        } else {
          throw new Error('Failed to fetch users');
        }
      } else if (user?.role === 'org_admin') {
        // Org admins should be able to invoice individual members and use team bulk options,
        // even if /api/teams/my-teams is empty.
        let teamsData = [];
        const myTeamsResponse = await fetch('/api/teams/my-teams', { headers: authHeaders() });
        if (myTeamsResponse.ok) {
          const myTeams = await myTeamsResponse.json();
          teamsData = Array.isArray(myTeams) ? myTeams : [];
        }

        if (teamsData.length === 0) {
          const orgsResponse = await fetch('/api/organizations/my-organizations', { headers: authHeaders() });
          if (orgsResponse.ok) {
            const raw = await orgsResponse.json();
            const orgs = Array.isArray(raw) ? raw : [];
            const orgIds = orgs
              .map(ou => ou.Organization?.id ?? ou.organization_id ?? ou.id)
              .filter(Boolean);
            teamsData = await fetchTeamsForOrganizations(orgIds);
          }
        }

        console.log('Fetched teams for org admin recipient lookup:', teamsData);
        const rosterRecipients = await fetchRosterRecipientsForTeams(teamsData);
        console.log('Roster recipients for org admin:', rosterRecipients.length);
        setUsers(rosterRecipients);
      } else {
        // Coaches and other users can invoice team members from teams they're a member of
        console.log('Fetching teams...');
        const teamsResponse = await fetch('/api/teams/my-teams', { headers: authHeaders() });
        
        console.log('Teams response status:', teamsResponse.status);
        
        if (teamsResponse.ok) {
          const teamsData = await teamsResponse.json();
          console.log('Available teams:', teamsData);
          
          // Fetch roster from ALL teams, not just primary
          const allRosterMembers = [];
          const memberIds = new Set(); // To avoid duplicates
          
          for (const team of teamsData) {
            try {
              const rosterResponse = await fetch(`/api/roster?teamId=${team.id}`, { headers: authHeaders() });
              
              if (rosterResponse.ok) {
                const rosterData = await rosterResponse.json();
                console.log(`✓ Fetched ${rosterData.length} members from ${team.name}`);
                
                // Add members to list, avoiding duplicates
                rosterData.forEach(member => {
                  if (!memberIds.has(member.id)) {
                    memberIds.add(member.id);
                    allRosterMembers.push({
                      id: member.id,
                      name: member.name,
                      email: member.email,
                      role: member.role,
                      type: 'user',
                      teamName: team.name // Add team name for reference
                    });
                  }
                });
              } else if (rosterResponse.status === 403) {
                // User doesn't have access to this team - skip silently
                console.log(`⚠️ No access to ${team.name} - skipping`);
              } else {
                console.warn(`Failed to fetch roster for ${team.name}: ${rosterResponse.status}`);
              }
            } catch (error) {
              // Network error or other issue - skip this team
              console.log(`⚠️ Could not fetch roster for ${team.name} - skipping`);
            }
          }
          
          console.log('Total roster members from all teams:', allRosterMembers.length);
          console.log('All roster members:', allRosterMembers);
          setUsers(allRosterMembers);
        } else {
          const errorData = await teamsResponse.json();
          console.error('Teams error:', errorData);
          throw new Error('Failed to fetch teams: ' + errorData.error);
        }
      }
    } catch (error) {
      console.error('Error fetching recipients:', error);
      // Fallback to demo data - using actual database user IDs
      setUsers([
        { id: 111, name: 'Coach Mike', email: 'coach@team.com', role: 'coach', type: 'user' },
        { id: 112, name: 'Parent Sarah', email: 'parent@team.com', role: 'parent', type: 'user' },
        { id: 113, name: 'Player Jake', email: 'player@team.com', role: 'player', type: 'user' }
      ]);
    }
  };
  const [activeTab, setActiveTab] = useState('all');

  // Fetch data on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch invoices
        const invoicesResponse = await fetch('/api/invoices/my-invoices', { headers: authHeaders() });
        
        if (invoicesResponse.ok) {
          const invoicesData = await invoicesResponse.json();
          setInvoices(invoicesData);
        }

        // Fetch teams
        const teamsResponse = await fetch('/api/teams/my-teams', { headers: authHeaders() });
        let teamsData = [];
        if (teamsResponse.ok) {
          const rawTeams = await teamsResponse.json();
          teamsData = Array.isArray(rawTeams) ? rawTeams : [];
        }

        if (user?.role === 'org_admin' || user?.role === 'admin' || user?.role === 'app_manager') {
          const orgsResponse = await fetch('/api/organizations/my-organizations', { headers: authHeaders() });
          if (orgsResponse.ok) {
            const raw = await orgsResponse.json();
            const list = Array.isArray(raw) ? raw : [];
            const orgs = list.map(ou => ({
              id: ou.Organization?.id ?? ou.organization_id ?? ou.id,
              name: ou.Organization?.name ?? ou.name ?? 'Organization'
            })).filter(o => o.id);
            setOrganizations(orgs);

            if (teamsData.length === 0 && orgs.length > 0) {
              teamsData = await fetchTeamsForOrganizations(orgs.map(org => org.id));
            }
          }
        }
        setTeams(teamsData);

        // Fetch recipients based on user role
        await fetchRecipients();
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user?.role]);

  const handleCreateInvoice = async (invoiceData) => {
    try {
      // Handle split among team - create multiple invoices
      if (invoiceData.split_among_team && invoiceData.to_user_id === 'ALL_PLAYERS') {
        console.log('Creating team split invoice...');
        
        const response = await fetch('/api/invoices/split-team', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            ...invoiceData,
            team_id: invoiceData.selected_team_id
          })
        });

        if (response.ok) {
          const result = await response.json();
          console.log(`Created ${result.invoices.length} invoices for players`);
          
          // Refresh invoice list
          const invoicesResponse = await fetch('/api/invoices/my-invoices', {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });
          
          if (invoicesResponse.ok) {
            const invoicesData = await invoicesResponse.json();
            setInvoices(invoicesData);
          }
          
          setShowCreateModal(false);
          toast.success(`Successfully created ${result.invoices.length} invoices for players!`);
        } else {
          const error = await response.json();
          console.error('Error creating team split invoice:', error);
          toast.error(`Error: ${error.message || 'Failed to create invoices'}`);
        }
        return;
      }

      // Handle bulk invoice age group (org_admin)
      if (invoiceData.bulk_invoice_age_group && invoiceData.to_user_id === 'BULK_AGE_GROUP') {
        if (!invoiceData.selected_organization_id || !invoiceData.selected_age_group_id) {
          toast.error('Error: Please select an organization and age group.');
          return;
        }
        const response = await fetch('/api/invoices/bulk-age-group', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            title: invoiceData.title,
            description: invoiceData.description,
            amount: parseFloat(invoiceData.amount),
            tax_amount: parseFloat(invoiceData.tax_amount || 0),
            due_date: invoiceData.due_date,
            program_end_date: invoiceData.program_end_date || null,
            organization_id: parseInt(invoiceData.selected_organization_id, 10),
            age_group_id: parseInt(invoiceData.selected_age_group_id, 10),
            notes: invoiceData.notes,
            payment_plan_enabled: invoiceData.payment_plan_enabled,
            payment_plan_options: invoiceData.payment_plan_options,
            allowed_payment_methods: invoiceData.allowed_payment_methods || ['stripe']
          })
        });
        const data = await response.json().catch(() => ({}));
        if (response.ok) {
          const invRes = await fetch('/api/invoices/my-invoices', {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          });
          if (invRes.ok) {
            const list = await invRes.json();
            setInvoices(list);
          }
          setShowCreateModal(false);
          toast.success(`Successfully created ${data.invoices?.length ?? 0} invoices for age group "${data.ageGroup?.name ?? ''}"! Each player received an invoice for $${invoiceData.amount}.`);
        } else {
          console.error('Error creating age group invoices:', data);
          toast.error(data.message || 'Failed to create age group invoices');
        }
        return;
      }

      // Handle bulk invoice team - create individual invoices for each player
      if (invoiceData.bulk_invoice_team && invoiceData.to_user_id === 'BULK_TEAM') {
        console.log('Creating bulk team invoice...');
        
        // Validate that a team is selected
        if (!invoiceData.selected_team_id) {
          toast.error('Error: Please select a team for bulk invoicing');
          return;
        }

        const response = await fetch('/api/invoices/bulk-team', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            ...invoiceData,
            team_id: parseInt(invoiceData.selected_team_id)
          })
        });

        if (response.ok) {
          const result = await response.json();
          console.log(`Created ${result.invoices.length} individual invoices for team ${result.team.name}`);
          
          // Refresh invoice list
          const invoicesResponse = await fetch('/api/invoices', {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });
          
          if (invoicesResponse.ok) {
            const invoicesData = await invoicesResponse.json();
            setInvoices(invoicesData);
          }
          
          setShowCreateModal(false);
          toast.success(`Successfully created ${result.invoices.length} individual invoices for team "${result.team.name}"! Each player received an invoice for $${invoiceData.amount}.`);
        } else {
          const error = await response.json();
          console.error('Error creating bulk team invoice:', error);
          toast.error(`Error: ${error.message || 'Failed to create bulk invoices'}`);
        }
        return;
      }

      // Handle single recipient invoice — only users are valid (not teams)
      if (invoiceData.to_user_id && String(invoiceData.to_user_id).startsWith('team_')) {
        toast.error('To invoice a team, please use "Bulk invoice team" or "Split cost among all players" above, then select the team.');
        return;
      }

      const invoicePayload = {
        ...invoiceData,
        to_user_id: invoiceData.to_user_id,
        ...(teams?.length > 0 && { teamId: teams[0].id })
      };

      console.log('Creating invoice with payload:', invoicePayload);

      const response = await fetch('/api/invoices', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(invoicePayload)
      });

      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        setInvoices(prev => [data, ...prev]);
        setShowCreateModal(false);
      } else {
        console.error('Error creating invoice:', data);
        toast.error(data.message || data.errors?.[0]?.msg || 'Failed to create invoice');
      }
    } catch (error) {
      console.error('Error creating invoice:', error);
      toast.error(error.message || 'Failed to create invoice');
    }
  };

  const handleUpdateInvoice = async (invoiceId, updates) => {
    try {
      const response = await fetch(`/api/invoices/${invoiceId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(updates)
      });

      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        setInvoices(prev => prev.map(inv =>
          inv.id === invoiceId ? data : inv
        ));
        return data;
      }
      const message = data.message || data.errors?.[0]?.msg || 'Failed to update invoice';
      throw new Error(message);
    } catch (error) {
      console.error('Error updating invoice:', error);
      throw error;
    }
  };

  const handleDeleteInvoice = async (invoiceId) => {
    try {
      const response = await fetch(`/api/invoices/${invoiceId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        setInvoices(prev => prev.filter(inv => inv.id !== invoiceId));
      }
    } catch (error) {
      console.error('Error deleting invoice:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'overdue':
        return 'bg-red-100 text-red-800';
      case 'sent':
        return 'bg-blue-100 text-blue-800';
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'paid':
        return <CheckCircleIcon className="h-5 w-5 text-green-600" />;
      case 'overdue':
        return <ExclamationTriangleIcon className="h-5 w-5 text-red-600" />;
      case 'sent':
        return <PaperAirplaneIcon className="h-5 w-5 text-blue-600" />;
      case 'draft':
        return <DocumentTextIcon className="h-5 w-5 text-gray-600" />;
      default:
        return <ClockIcon className="h-5 w-5 text-gray-600" />;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const filteredInvoices = invoices.filter(invoice => {
    if (activeTab === 'all') return true;
    return invoice.status === activeTab;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center">
              <DocumentTextIcon className="h-8 w-8 text-blue-600 mr-3" />
              Invoicing
            </h1>
            <p className="text-gray-600 mt-1">
              Manage invoices and payments
            </p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Create Invoice
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <DocumentTextIcon className="h-8 w-8 text-blue-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Total Invoices</p>
              <p className="text-2xl font-bold text-gray-900">{invoices.length}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <CheckCircleIcon className="h-8 w-8 text-green-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Paid</p>
              <p className="text-2xl font-bold text-gray-900">
                {invoices.filter(inv => inv.status === 'paid').length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <ExclamationTriangleIcon className="h-8 w-8 text-red-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Overdue</p>
              <p className="text-2xl font-bold text-gray-900">
                {invoices.filter(inv => inv.status === 'overdue').length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <CurrencyDollarIcon className="h-8 w-8 text-purple-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Total Amount</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(invoices.reduce((sum, inv) => sum + parseFloat(inv.total_amount || 0), 0))}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex space-x-4">
          {[
            { id: 'all', name: 'All Invoices', count: invoices.length },
            { id: 'sent', name: 'Sent', count: invoices.filter(inv => inv.status === 'sent').length },
            { id: 'overdue', name: 'Overdue', count: invoices.filter(inv => inv.status === 'overdue').length },
            { id: 'paid', name: 'Paid', count: invoices.filter(inv => inv.status === 'paid').length }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center ${
                activeTab === tab.id
                  ? 'bg-blue-100 text-blue-700'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.name}
              {tab.count > 0 && (
                <span className={`ml-2 px-2 py-0.5 text-xs rounded-full ${
                  activeTab === tab.id 
                    ? 'bg-blue-200 text-blue-800' 
                    : 'bg-gray-200 text-gray-600'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Invoices List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">
            {filteredInvoices.length} {filteredInvoices.length === 1 ? 'Invoice' : 'Invoices'}
          </h2>
        </div>
        <div className="divide-y divide-gray-200">
          {filteredInvoices.length === 0 ? (
            <div className="px-6 py-12 text-center">
              <DocumentTextIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No invoices found</h3>
              <p className="text-gray-500 mb-4">Get started by creating your first invoice.</p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
              >
                Create Invoice
              </button>
            </div>
          ) : (
            filteredInvoices.map((invoice) => (
              <div key={invoice.id} className="px-6 py-4 hover:bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="flex-shrink-0">
                      {getStatusIcon(invoice.status)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <h3 className="text-lg font-medium text-gray-900 truncate">
                          {invoice.title}
                        </h3>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(invoice.status)}`}>
                          {invoice.status}
                        </span>
                        {invoice.metadata?.stripe?.last_payment_status === 'failed' && invoice.status !== 'paid' && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                            ⚠ Payment Failed
                          </span>
                        )}
                      </div>
                      <div className="mt-1 flex items-center space-x-4 text-sm text-gray-500">
                        <span>#{invoice.invoice_number}</span>
                        <span>Due: {formatDate(invoice.due_date)}</span>
                        {invoice.paid_date && (
                          <span className="text-green-600 font-medium">✓ Paid: {formatDate(invoice.paid_date)}</span>
                        )}
                        {invoice.payment_method && (
                          <span className="text-blue-600">via {invoice.payment_method.replace('_', ' ')}</span>
                        )}
                      </div>
                      {invoice.metadata?.stripe?.last_failure_message && invoice.status !== 'paid' && (
                        <p className="mt-1 text-xs text-red-600">
                          Last failure: {invoice.metadata.stripe.last_failure_message}
                        </p>
                      )}
                      {invoice.description && (
                        <p className="mt-1 text-sm text-gray-600">{invoice.description}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="text-right">
                      <p className="text-lg font-medium text-gray-900">
                        {formatCurrency(invoice.total_amount)}
                      </p>
                      {invoice.payment_method && (
                        <p className="text-sm text-gray-500 capitalize">
                          {invoice.payment_method.replace('_', ' ')}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center space-x-2">
                      {/* Pay Now button for unpaid invoices - only show if user is the recipient */}
                      {invoice.status !== 'paid' && invoice.status !== 'cancelled' && invoice.to_user_id === user?.id && (
                        <button
                          onClick={() => {
                            setSelectedInvoice(invoice);
                            setShowPaymentModal(true);
                          }}
                          className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
                        >
                          Pay Now
                        </button>
                      )}
                      
                      <button
                        onClick={() => {
                          setSelectedInvoice(invoice);
                          setShowViewModal(true);
                        }}
                        className="p-2 text-gray-400 hover:text-gray-600"
                        title="View Details"
                      >
                        <EyeIcon className="h-5 w-5" />
                      </button>
                      {invoice.status !== 'paid' && (
                        <div className="flex items-center space-x-2">
                          <label className="text-xs text-gray-600">Remind:</label>
                          <select
                            value={remindTimingByInvoice[invoice.id] || 'now'}
                            onChange={(e) => setRemindTimingByInvoice(prev => ({ ...prev, [invoice.id]: e.target.value }))}
                            className="px-2 py-1 text-xs border border-gray-300 rounded"
                          >
                            <option value="now">Now</option>
                            <option value="day_of">Day of</option>
                            <option value="day_before">1 day before</option>
                          </select>
                          <button
                            onClick={async () => {
                              try {
                                const timing = remindTimingByInvoice[invoice.id] || 'now';
                                const resp = await fetch(`/api/emails/invoices/${invoice.id}/remind`, {
                                  method: 'POST',
                                  headers: {
                                    'Content-Type': 'application/json',
                                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                                  },
                                  body: JSON.stringify({ timing })
                                });
                                if (!resp.ok) {
                                  const err = await resp.json();
                                  throw new Error(err.message || 'Failed to send reminder');
                                }
                                toast.success('Invoice reminder sent');
                              } catch (e) {
                                console.error('Invoice remind failed', e);
                                toast.error(e.message || 'Failed to send reminder');
                              }
                            }}
                            className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                          >
                            Send
                          </button>
                        </div>
                      )}
                      <button
                        onClick={() => {
                          setEditingInvoice(invoice);
                          setShowEditModal(true);
                        }}
                        className="p-2 text-gray-400 hover:text-gray-600"
                        title="Edit Invoice"
                      >
                        <PencilIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => handleDeleteInvoice(invoice.id)}
                        className="p-2 text-gray-400 hover:text-red-600"
                        title="Delete Invoice"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Create Invoice Modal */}
      {showCreateModal && (
        <CreateInvoiceModal
          teams={teams}
          users={users}
          organizations={organizations}
          onClose={() => setShowCreateModal(false)}
          onCreate={handleCreateInvoice}
        />
      )}

      {/* Payment Modal */}
      {showPaymentModal && selectedInvoice && (
        <PaymentModal
          invoice={selectedInvoice}
          onClose={() => setShowPaymentModal(false)}
          onPayment={async (paymentData) => {
            try {
              // Update the invoice with payment information
              await handleUpdateInvoice(selectedInvoice.id, {
                status: 'paid',
                payment_method: paymentData.method,
                payment_reference: paymentData.reference,
                paid_date: new Date().toISOString()
              });
              
              // Refresh the invoice list to show updated status
              const response = await fetch('/api/invoices', {
                headers: {
                  'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
              });
              
              if (response.ok) {
                const updatedInvoices = await response.json();
                setInvoices(updatedInvoices);
              }
              
              setShowPaymentModal(false);
              setShowPaymentSuccess(true);
              // Hide success message after 3 seconds
              setTimeout(() => setShowPaymentSuccess(false), 3000);
            } catch (error) {
              console.error('Error processing payment:', error);
            }
          }}
        />
      )}

      {/* View Invoice Modal */}
      {showViewModal && selectedInvoice && (
        <ViewInvoiceModal
          invoice={selectedInvoice}
          onClose={() => {
            setShowViewModal(false);
            setSelectedInvoice(null);
          }}
          formatCurrency={formatCurrency}
          formatDate={formatDate}
          getStatusColor={getStatusColor}
        />
      )}

      {/* Edit Invoice Modal */}
      {showEditModal && editingInvoice && (
        <EditInvoiceModal
          invoice={editingInvoice}
          onClose={() => {
            setShowEditModal(false);
            setEditingInvoice(null);
          }}
          onSave={async (updates) => {
            try {
              await handleUpdateInvoice(editingInvoice.id, updates);
              toast.success('Invoice updated');
              setShowEditModal(false);
              setEditingInvoice(null);
            } catch (error) {
              toast.error(error.message || 'Failed to update invoice');
            }
          }}
        />
      )}

      {/* Payment Success Notification */}
      {showPaymentSuccess && (
        <div className="fixed top-4 right-4 z-50">
          <div className="bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg flex items-center space-x-2">
            <CheckCircleIcon className="h-5 w-5" />
            <span>Payment processed successfully!</span>
          </div>
        </div>
      )}
    </div>
  );
};

// Create Invoice Modal Component
const CreateInvoiceModal = ({ teams, users, organizations = [], onClose, onCreate }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    amount: '',
    tax_amount: '0',
    to_user_id: '',
    due_date: '',
    program_end_date: '',
    line_items: [],
    payment_plan_enabled: false,
    payment_plan_options: [],
    split_among_team: false,
    bulk_invoice_team: false,
    bulk_invoice_age_group: false,
    selected_team_id: '',
    selected_organization_id: '',
    selected_age_group_id: '',
    allowed_payment_methods: []
  });
  const [paymentMethodsInitialized, setPaymentMethodsInitialized] = useState(false);
  const [ageGroups, setAgeGroups] = useState([]);
  const showAgeGroupOption = ['org_admin', 'admin', 'app_manager'].includes(user?.role);

  const senderPaymentMethods = Array.isArray(user?.payment_preferences?.accepted_methods)
    ? user.payment_preferences.accepted_methods.filter(method => SUPPORTED_PAYMENT_METHODS.includes(method))
    : ['stripe'];
  const availableSenderMethods = senderPaymentMethods.length ? senderPaymentMethods : ['stripe'];

  useEffect(() => {
    if (!paymentMethodsInitialized) {
      setFormData(prev => ({
        ...prev,
        allowed_payment_methods: ['stripe'] // Always default to Stripe only
      }));
      setPaymentMethodsInitialized(true);
    }
  }, [paymentMethodsInitialized]);

  useEffect(() => {
    if (!formData.selected_organization_id) {
      setAgeGroups([]);
      setFormData(prev => ({ ...prev, selected_age_group_id: '' }));
      return;
    }
    const fetchAgeGroups = async () => {
      try {
        const res = await fetch(
          `/api/age-groups?organizationId=${formData.selected_organization_id}`,
          { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
        );
        if (res.ok) {
          const data = await res.json();
          setAgeGroups(Array.isArray(data) ? data : []);
        } else {
          setAgeGroups([]);
        }
      } catch {
        setAgeGroups([]);
      }
    };
    fetchAgeGroups();
  }, [formData.selected_organization_id]);

  const PLAN_OPTION_CANDIDATES = [2, 3, 4, 6, 9, 12];

  const parseLocalDate = (value) => {
    if (!value || typeof value !== 'string') return null;
    const parts = value.split('-').map((v) => parseInt(v, 10));
    if (parts.length !== 3 || parts.some(Number.isNaN)) return null;
    return new Date(parts[0], parts[1] - 1, parts[2]);
  };

  const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

  const addMonthsClamped = (baseDate, monthDelta) => {
    const result = new Date(baseDate);
    const day = result.getDate();
    result.setDate(1);
    result.setMonth(result.getMonth() + monthDelta);
    const lastDay = new Date(result.getFullYear(), result.getMonth() + 1, 0).getDate();
    result.setDate(Math.min(day, lastDay));
    return result;
  };

  const dueDateObj = parseLocalDate(formData.due_date);
  const programEndDateObj = parseLocalDate(formData.program_end_date);
  const planEndDateObj = (() => {
    if (!dueDateObj) return null;
    if (programEndDateObj && programEndDateObj > dueDateObj) return programEndDateObj;
    return dueDateObj;
  })();

  const maxInstallmentsAllowed = (() => {
    if (!planEndDateObj) return 1;
    const today = startOfDay(new Date());
    const end = startOfDay(planEndDateObj);
    if (end < today) return 1;
    let max = 1;
    for (let months = 2; months <= 12; months++) {
      const firstDueDate = addMonthsClamped(end, -(months - 1));
      if (startOfDay(firstDueDate) >= today) {
        max = months;
      } else {
        break;
      }
    }
    return max;
  })();

  const availablePlanOptions = (() => {
    if (maxInstallmentsAllowed < 2) return [];
    const options = PLAN_OPTION_CANDIDATES.filter(months => months <= maxInstallmentsAllowed);
    if (options.length > 0) return options;
    return [maxInstallmentsAllowed];
  })();

  useEffect(() => {
    if (!formData.payment_plan_enabled) return;
    setFormData(prev => {
      const filtered = (prev.payment_plan_options || []).filter(months => months <= maxInstallmentsAllowed && months >= 2);
      const nextOptions = filtered.length > 0 ? filtered : (availablePlanOptions.length > 0 ? [availablePlanOptions[0]] : []);
      if (
        filtered.length === (prev.payment_plan_options || []).length &&
        nextOptions.length === (prev.payment_plan_options || []).length
      ) {
        return prev;
      }
      return {
        ...prev,
        payment_plan_options: nextOptions
      };
    });
  }, [formData.payment_plan_enabled, maxInstallmentsAllowed, availablePlanOptions.length]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const methods = (formData.allowed_payment_methods && formData.allowed_payment_methods.length)
      ? formData.allowed_payment_methods
      : availableSenderMethods;
    onCreate({
      ...formData,
      allowed_payment_methods: methods
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Create Invoice</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Title</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                required
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={3}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Amount</label>
              <input
                type="number"
                step="0.01"
                name="amount"
                value={formData.amount}
                onChange={handleInputChange}
                required
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">Recipient</label>
                {users.length === 0 && (
                  <button
                    type="button"
                    onClick={() => window.location.reload()}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    Refresh
                  </button>
                )}
              </div>
              
              {/* Team invoicing options */}
              <div className="mb-3 space-y-3">
                {/* Split among team option */}
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.split_among_team}
                      onChange={(e) => {
                        setFormData(prev => ({
                          ...prev,
                          split_among_team: e.target.checked,
                          bulk_invoice_team: false,
                          bulk_invoice_age_group: false,
                          to_user_id: e.target.checked ? 'ALL_PLAYERS' : ''
                        }));
                      }}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label className="ml-2 block text-sm font-medium text-gray-900">
                      Split cost among all players
                    </label>
                  </div>
                  {formData.split_among_team && (
                    <div className="mt-3">
                      <p className="text-sm text-blue-700 mb-2">
                        💡 Each player will receive an individual invoice for their share of the total cost.
                      </p>
                      <div className="mb-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Select Team
                        </label>
                        <select
                          value={formData.selected_team_id || ''}
                          onChange={(e) => {
                            setFormData(prev => ({
                              ...prev,
                              selected_team_id: e.target.value
                            }));
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          required
                        >
                          <option value="">Choose a team...</option>
                          {teams.map(team => (
                            <option key={team.id} value={team.id}>
                              {team.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      {formData.amount && formData.selected_team_id && (
                        <p className="text-sm text-blue-700 font-medium">
                          Players in selected team will each pay: ${(parseFloat(formData.amount || 0) / teams.find(t => t.id == formData.selected_team_id)?.currentPlayers || 1).toFixed(2)}
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* Bulk invoice team option (for org admins) */}
                {(() => {
                  const shouldShow = ['org_admin', 'coach', 'assistant_coach', 'admin'].includes(user?.role) || users.some(u => u.type === 'team');
                  console.log('Bulk invoice option should show:', shouldShow, 'user role:', user?.role, 'users with team type:', users.filter(u => u.type === 'team'));
                  return shouldShow;
                })() && (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.bulk_invoice_team}
                        onChange={(e) => {
                          setFormData(prev => ({
                            ...prev,
                            bulk_invoice_team: e.target.checked,
                            split_among_team: false,
                            bulk_invoice_age_group: false,
                            to_user_id: e.target.checked ? 'BULK_TEAM' : '',
                            selected_team_id: e.target.checked ? (prev.selected_team_id || '') : ''
                          }));
                        }}
                        className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                      />
                      <label className="ml-2 block text-sm font-medium text-gray-900">
                        Bulk invoice team (same amount per player)
                      </label>
                    </div>
                    {formData.bulk_invoice_team && (
                      <div className="mt-3">
                        <p className="text-sm text-green-700 mb-2">
                          💡 Each player will receive an individual invoice for the full amount specified.
                        </p>
                        <div className="mb-2">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Select Team
                          </label>
                          <select
                            value={formData.selected_team_id || ''}
                            onChange={(e) => {
                              setFormData(prev => ({
                                ...prev,
                                selected_team_id: e.target.value
                              }));
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-green-500 focus:border-green-500"
                            required
                          >
                            <option value="">Choose a team...</option>
                            {teams.map(team => (
                              <option key={team.id} value={team.id}>
                                {team.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        {formData.amount && formData.selected_team_id && (
                          <span className="block text-sm font-medium text-green-700">
                            Each player will be invoiced: ${formData.amount}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Invoice age group (org_admin only) */}
                {showAgeGroupOption && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.bulk_invoice_age_group}
                        onChange={(e) => {
                          setFormData(prev => ({
                            ...prev,
                            bulk_invoice_age_group: e.target.checked,
                            split_among_team: false,
                            bulk_invoice_team: false,
                            to_user_id: e.target.checked ? 'BULK_AGE_GROUP' : '',
                            selected_organization_id: e.target.checked ? (prev.selected_organization_id || '') : '',
                            selected_age_group_id: e.target.checked ? (prev.selected_age_group_id || '') : ''
                          }));
                        }}
                        className="h-4 w-4 text-amber-600 focus:ring-amber-500 border-gray-300 rounded"
                      />
                      <label className="ml-2 block text-sm font-medium text-gray-900">
                        Invoice whole age group (e.g. 14U Spring/Summer dues)
                      </label>
                    </div>
                    {formData.bulk_invoice_age_group && (
                      <div className="mt-3">
                        <p className="text-sm text-amber-700 mb-2">
                          Each player in the selected age group will receive an individual invoice for the amount specified.
                        </p>
                        <div className="mb-2">
                          <label className="block text-sm font-medium text-gray-700 mb-1">Organization</label>
                          <select
                            value={formData.selected_organization_id || ''}
                            onChange={(e) => setFormData(prev => ({ ...prev, selected_organization_id: e.target.value, selected_age_group_id: '' }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-amber-500 focus:border-amber-500"
                            required
                          >
                            <option value="">Choose organization...</option>
                            {organizations.map(org => (
                              <option key={org.id} value={org.id}>{org.name}</option>
                            ))}
                          </select>
                        </div>
                        <div className="mb-2">
                          <label className="block text-sm font-medium text-gray-700 mb-1">Age group</label>
                          <select
                            value={formData.selected_age_group_id || ''}
                            onChange={(e) => setFormData(prev => ({ ...prev, selected_age_group_id: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-amber-500 focus:border-amber-500"
                            required
                            disabled={!formData.selected_organization_id}
                          >
                            <option value="">Choose age group...</option>
                            {ageGroups.map(ag => (
                              <option key={ag.id} value={ag.id}>{ag.name}</option>
                            ))}
                          </select>
                        </div>
                        {formData.amount && formData.selected_age_group_id && (
                          <span className="block text-sm font-medium text-amber-700">
                            Each player in this age group will be invoiced: ${formData.amount}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {!formData.split_among_team && !formData.bulk_invoice_team && !formData.bulk_invoice_age_group && (
                <select
                  name="to_user_id"
                  value={formData.to_user_id}
                  onChange={handleInputChange}
                  required
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select recipient ({users.length} available)</option>
                  {users.length === 0 ? (
                    <option value="" disabled>No recipients available - try refreshing</option>
                  ) : (
                    users.map(recipient => (
                      <option key={recipient.id} value={recipient.id}>
                        {recipient.type === 'team' 
                          ? `🏆 ${recipient.name} (Team)`
                          : recipient.teamName
                            ? `👤 ${recipient.name} (${recipient.role}) - ${recipient.teamName}`
                            : `👤 ${recipient.name} (${recipient.role})`
                        }
                      </option>
                    ))
                  )}
                </select>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Due Date</label>
              <input
                type="date"
                name="due_date"
                value={formData.due_date}
                onChange={handleInputChange}
                required
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Program End Date (Optional)</label>
              <input
                type="date"
                name="program_end_date"
                value={formData.program_end_date}
                onChange={handleInputChange}
                min={formData.due_date || undefined}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                If set, payment plans are scheduled to end on this date. Otherwise Due Date is used.
              </p>
            </div>
            
            {/* Payment Method Info */}
            <div className="border-t border-gray-200 pt-4 mt-4">
              <h4 className="text-sm font-semibold text-gray-900 mb-2">Payment Method</h4>
              <p className="text-xs text-gray-500 mb-3">
                Invoices are paid securely through Stripe using credit or debit cards.
              </p>
              <div className="flex items-center space-x-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-md">
                <input
                  type="checkbox"
                  checked={true}
                  disabled
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="text-sm text-gray-700 font-medium">Stripe (Card)</span>
              </div>
            </div>
            
            {/* Payment Plan Option */}
            <div className="border-t border-gray-200 pt-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    name="payment_plan_enabled"
                    checked={formData.payment_plan_enabled}
                    onChange={(e) => {
                      setFormData(prev => ({
                        ...prev,
                        payment_plan_enabled: e.target.checked,
                        payment_plan_options: e.target.checked
                          ? (availablePlanOptions.length ? [availablePlanOptions[0]] : [])
                          : []
                      }));
                    }}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 block text-sm font-medium text-gray-700">
                    Offer Payment Plan
                  </label>
                </div>
                {formData.payment_plan_enabled && (
                  <div className="mt-3 ml-6">
                    <p className="text-sm text-gray-600 mb-2">
                      Allow recipient to pay in installments ending by {planEndDateObj ? planEndDateObj.toLocaleDateString() : 'the selected date'}:
                    </p>
                    {availablePlanOptions.length === 0 ? (
                      <p className="text-sm text-amber-600">
                        No monthly plan fits this date window. Choose a later Due Date or Program End Date.
                      </p>
                    ) : (
                    <div className="space-y-2">
                      {availablePlanOptions.map((months) => (
                        <div key={months} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={formData.payment_plan_options.includes(months)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setFormData(prev => ({
                                  ...prev,
                                  payment_plan_options: [...prev.payment_plan_options, months].sort((a, b) => a - b)
                                }));
                              } else {
                                setFormData(prev => ({
                                  ...prev,
                                  payment_plan_options: prev.payment_plan_options.filter(m => m !== months)
                                }));
                              }
                            }}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <label className="ml-2 block text-sm text-gray-700">
                            {months} monthly payments (${(parseFloat(formData.amount || 0) / months).toFixed(2)}/month)
                          </label>
                        </div>
                      ))}
                    </div>
                    )}
                  </div>
                )}
            </div>
            
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
              >
                Create Invoice
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Payment Modal Component
const PaymentModal = ({ invoice, onClose, onPayment }) => {
  const [selectedMethod, setSelectedMethod] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState(null);

  // Get sender's accepted payment methods
  const senderPaymentPrefs = invoice.FromUser?.payment_preferences || {
    accepted_methods: SUPPORTED_PAYMENT_METHODS,
    preferred_method: 'stripe'
  };

  const invoiceAllowedMethods = Array.isArray(invoice.allowed_payment_methods)
    ? invoice.allowed_payment_methods.filter(method => SUPPORTED_PAYMENT_METHODS.includes(method))
    : null;

  const availableMethods = (invoiceAllowedMethods && invoiceAllowedMethods.length
    ? invoiceAllowedMethods
    : senderPaymentPrefs.accepted_methods || SUPPORTED_PAYMENT_METHODS
  )
    .filter(method => SUPPORTED_PAYMENT_METHODS.includes(method))
    .filter((method, index, arr) => arr.indexOf(method) === index);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedMethod) {
      setPaymentError('Please choose a payment method to continue.');
      return;
    }

    if (['stripe'].includes(selectedMethod)) {
      // Dedicated payment components handle processing for Stripe
      return;
    }

    // Only Stripe is supported
    setPaymentError('Please select Stripe as your payment method.');
  };

  const handlePaymentMethodChange = (method) => {
    setSelectedMethod(method);
    setPaymentError(null);
  };

  const handleStripePaymentSuccess = (paymentResult) => {
    const paymentData = {
      method: 'stripe',
      reference: paymentResult.paymentIntentId,
      amount: invoice.total_amount,
      receiptUrl: paymentResult.receiptUrl
    };
    onPayment(paymentData);
  };

  const handleStripePaymentError = (error) => {
    setPaymentError(error.message || 'Stripe payment failed');
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Process Payment</h3>
          
          {/* Show dedicated payment form for card processors */}
          {selectedMethod === 'stripe' ? (
            <StripePaymentForm
              invoice={invoice}
              onPaymentSuccess={handleStripePaymentSuccess}
              onPaymentError={handleStripePaymentError}
              onCancel={onClose}
            />
          ) : (
            <>
              {/* Invoice Summary */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <div className="flex justify-between items-start mb-2">
                  <h4 className="font-medium text-gray-900">{invoice.title}</h4>
                  <span className="text-sm text-gray-500">#{invoice.invoice_number}</span>
                </div>
                <p className="text-sm text-gray-600 mb-2">{invoice.description}</p>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Amount Due:</span>
                  <span className="text-xl font-bold text-gray-900">${invoice.total_amount}</span>
                </div>
                {invoice.due_date && (
                  <div className="flex justify-between items-center mt-1">
                    <span className="text-sm text-gray-600">Due Date:</span>
                    <span className="text-sm text-gray-900">{new Date(invoice.due_date).toLocaleDateString()}</span>
                  </div>
                )}
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Choose Payment Method
                    <span className="text-sm text-gray-500 ml-2">
                      (Provided by {invoice.FromUser?.name || 'sender'})
                    </span>
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    {availableMethods.includes('stripe') && (
                      <button
                        type="button"
                        onClick={() => handlePaymentMethodChange('stripe')}
                        className={`p-3 border-2 rounded-lg text-center transition-colors ${
                          selectedMethod === 'stripe' 
                            ? 'border-blue-500 bg-blue-50 text-blue-700' 
                            : 'border-gray-300 hover:border-gray-400'
                        }`}
                      >
                        <div className="text-2xl mb-1">💠</div>
                        <div className="text-sm font-medium">Stripe (Card)</div>
                        <div className="text-xs text-gray-500 mt-1">
                          Secure card payment processed via Stripe
                        </div>
                      </button>
                    )}
                    {availableMethods.includes('bank_transfer') && (
                      <button
                        type="button"
                        onClick={() => handlePaymentMethodChange('bank_transfer')}
                        className={`p-3 border-2 rounded-lg text-center transition-colors ${
                          selectedMethod === 'bank_transfer' 
                            ? 'border-blue-500 bg-blue-50 text-blue-700' 
                            : 'border-gray-300 hover:border-gray-400'
                        }`}
                      >
                        <div className="text-2xl mb-1">🏦</div>
                        <div className="text-sm font-medium">Bank Transfer</div>
                      </button>
                    )}
                    {availableMethods.includes('zelle') && (
                      <button
                        type="button"
                        onClick={() => handlePaymentMethodChange('zelle')}
                        className={`p-3 border-2 rounded-lg text-center transition-colors ${
                          selectedMethod === 'zelle' 
                            ? 'border-blue-500 bg-blue-50 text-blue-700' 
                            : 'border-gray-300 hover:border-gray-400'
                        }`}
                      >
                        <div className="text-2xl mb-1">💚</div>
                        <div className="text-sm font-medium">Zelle</div>
                        {senderPaymentPrefs.zelle_email && (
                          <div className="text-xs text-gray-500 mt-1">{senderPaymentPrefs.zelle_email}</div>
                        )}
                      </button>
                    )}
                  </div>
                  
                  {availableMethods.length === 0 && (
                    <div className="text-center py-4 text-gray-500">
                      <p>No payment methods have been enabled for this invoice.</p>
                      <p className="text-sm">Please contact the sender to adjust their payment preferences.</p>
                    </div>
                  )}
                </div>

                {/* Error Message */}
                {paymentError && (
                  <div className="flex items-center p-4 bg-red-50 border border-red-200 rounded-lg">
                    <ExclamationTriangleIcon className="h-5 w-5 text-red-600 mr-2" />
                    <span className="text-sm text-red-700">{paymentError}</span>
                  </div>
                )}

                {/* Processing State */}
                {isProcessing && (
                  <div className="text-center py-4">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                    <p className="text-sm text-gray-600">Processing payment...</p>
                  </div>
                )}

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={onClose}
                    disabled={isProcessing}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={!selectedMethod || isProcessing}
                    className="px-6 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isProcessing ? 'Processing...' : `Pay $${invoice.total_amount}`}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// View Invoice Modal Component
const ViewInvoiceModal = ({ invoice, onClose, formatCurrency, formatDate, getStatusColor }) => {
  const allowedMethods = Array.isArray(invoice.allowed_payment_methods)
    ? invoice.allowed_payment_methods
    : [];

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-full max-w-lg shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="text-lg font-medium text-gray-900">{invoice.title}</h3>
              <p className="text-sm text-gray-500">#{invoice.invoice_number}</p>
            </div>
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(invoice.status)}`}>
              {invoice.status}
            </span>
          </div>

          {invoice.description && (
            <div className="mb-4">
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Description</h4>
              <p className="text-sm text-gray-800">{invoice.description}</p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">From</h4>
              <p className="text-sm text-gray-800">{invoice.FromUser?.name || '—'}</p>
              {invoice.FromUser?.email && (
                <p className="text-xs text-gray-500">{invoice.FromUser.email}</p>
              )}
            </div>
            <div>
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">To</h4>
              <p className="text-sm text-gray-800">{invoice.ToUser?.name || '—'}</p>
              {invoice.ToUser?.email && (
                <p className="text-xs text-gray-500">{invoice.ToUser.email}</p>
              )}
            </div>
          </div>

          <div className="border-t border-gray-200 pt-4 mb-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Amount</span>
              <span className="text-gray-900">{formatCurrency(invoice.amount || 0)}</span>
            </div>
            {parseFloat(invoice.tax_amount || 0) > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Tax</span>
                <span className="text-gray-900">{formatCurrency(invoice.tax_amount)}</span>
              </div>
            )}
            <div className="flex justify-between text-base font-medium border-t border-gray-100 pt-2">
              <span className="text-gray-900">Total</span>
              <span className="text-gray-900">{formatCurrency(invoice.total_amount || 0)}</span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
            <div>
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Due Date</h4>
              <p className="text-gray-800">{invoice.due_date ? formatDate(invoice.due_date) : '—'}</p>
            </div>
            {invoice.paid_date && (
              <div>
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Paid On</h4>
                <p className="text-gray-800">{formatDate(invoice.paid_date)}</p>
              </div>
            )}
            {invoice.payment_method && (
              <div>
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Payment Method</h4>
                <p className="text-gray-800 capitalize">{invoice.payment_method.replace('_', ' ')}</p>
              </div>
            )}
            {invoice.Team?.name && (
              <div>
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Team</h4>
                <p className="text-gray-800">{invoice.Team.name}</p>
              </div>
            )}
          </div>

          {allowedMethods.length > 0 && (
            <div className="mb-4">
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Allowed Payment Methods</h4>
              <p className="text-sm text-gray-800 capitalize">
                {allowedMethods.map(m => m.replace('_', ' ')).join(', ')}
              </p>
            </div>
          )}

          {invoice.metadata?.stripe?.last_payment_status === 'failed' && invoice.status !== 'paid' && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <h4 className="text-xs font-semibold text-red-700 uppercase tracking-wide mb-1">⚠ Last Payment Failed</h4>
              <p className="text-sm text-red-800">{invoice.metadata.stripe.last_failure_message || 'Payment was declined.'}</p>
              {invoice.metadata.stripe.last_failure_at && (
                <p className="text-xs text-red-600 mt-1">
                  {new Date(invoice.metadata.stripe.last_failure_at).toLocaleString()}
                </p>
              )}
            </div>
          )}

          {invoice.notes && (
            <div className="mb-4">
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Notes</h4>
              <p className="text-sm text-gray-800 whitespace-pre-wrap">{invoice.notes}</p>
            </div>
          )}

          <div className="flex justify-end pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Edit Invoice Modal Component
const EditInvoiceModal = ({ invoice, onClose, onSave }) => {
  const toDateInputValue = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const [formData, setFormData] = useState({
    title: invoice.title || '',
    description: invoice.description || '',
    amount: invoice.amount != null ? String(invoice.amount) : '',
    tax_amount: invoice.tax_amount != null ? String(invoice.tax_amount) : '0',
    due_date: toDateInputValue(invoice.due_date),
    notes: invoice.notes || '',
    status: invoice.status || 'draft'
  });
  const [isSaving, setIsSaving] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      await onSave({
        title: formData.title,
        description: formData.description,
        amount: parseFloat(formData.amount) || 0,
        tax_amount: parseFloat(formData.tax_amount) || 0,
        due_date: formData.due_date || null,
        notes: formData.notes,
        status: formData.status
      });
    } finally {
      setIsSaving(false);
    }
  };

  const isPaid = invoice.status === 'paid';

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-1">Edit Invoice</h3>
          <p className="text-sm text-gray-500 mb-4">#{invoice.invoice_number}</p>

          {isPaid && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-800">
              This invoice has already been paid. Edits to amount or due date may not be appropriate.
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Title</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                required
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={3}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700">Amount</label>
                <input
                  type="number"
                  step="0.01"
                  name="amount"
                  value={formData.amount}
                  onChange={handleInputChange}
                  required
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Tax</label>
                <input
                  type="number"
                  step="0.01"
                  name="tax_amount"
                  value={formData.tax_amount}
                  onChange={handleInputChange}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Due Date</label>
              <input
                type="date"
                name="due_date"
                value={formData.due_date}
                onChange={handleInputChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Status</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="draft">Draft</option>
                <option value="sent">Sent</option>
                <option value="overdue">Overdue</option>
                <option value="paid">Paid</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Notes</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                rows={2}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                disabled={isSaving}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSaving}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Invoicing;
